import useMinimumLoading from "../../hooks/useMinimumLoading";
import GroupSkeleton from "../../components/loading/GroupSkeleton";
import {AirportService, AirportResponse} from "../../services/AirportService";
import GroupAirportInput from "../../components/input/GroupAirportInput";
import {FormEvent, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {CategoryIconMap, CategoryLabelMap, DestinationCategory} from "../../models/DestinationCategory";
import {GroupSessionService} from "../../services/GroupSessionService";
import {getCurrentUserId} from "../../utils/auth_utils";

export const localDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export default function SharePreferencesScreen() {
    const {sessionId} = useParams<{sessionId: string}>();
    const navigate = useNavigate();
    const today = new Date();
    const [start, setStart] = useState(localDate(today));
    const [end, setEnd] = useState(localDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7)));
    const [budget, setBudget] = useState(250);
    const [minDays, setMinDays] = useState(3);
    const [maxDays, setMaxDays] = useState(5);
    const [categories, setCategories] = useState<DestinationCategory[]>(Object.values(DestinationCategory));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [airports, setAirports] = useState<AirportResponse[]>([]);
    const [departure, setDeparture] = useState<string[]>([]);
    const [destinations, setDestinations] = useState<string[]>([]);
    const inFlight = useRef(false);

    useEffect(() => {
        let active = true;
        if (!sessionId) return;
        Promise.all([GroupSessionService.getSession(sessionId), GroupSessionService.getPreferences(sessionId), AirportService.getAirports()])
            .then(([session, preferences, airportList]) => {
                if (!active) return;
                setAirports(airportList);
                setDeparture(session.departureAirport ? [session.departureAirport] : []);
                const own = preferences.find(p => p.userId === getCurrentUserId());
                if (own) {
                    if (own.departureAirport) setDeparture([own.departureAirport]);
                    setDestinations(own.destinationAirports || []);
                    setStart(own.timeRangeStart); setEnd(own.timeRangeEnd);
                    setBudget(own.budgetMax); setMinDays(own.travelDurationMin); setMaxDays(own.travelDurationMax);
                    setCategories(own.categories as DestinationCategory[]);
                }
                setLoading(false);
            }).catch(() => { if (active) setError("Could not load your preferences. Please reload to try again."); });
        return () => { active = false; };
    }, [sessionId, navigate]);

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (!sessionId || loading || inFlight.current) return;
        if (!departure.length) { setError("Bitte wähle deinen Abflughafen."); return; }
        if (start > end || minDays > maxDays || (Date.parse(end) - Date.parse(start)) / 86400000 < minDays) {
            setError("Choose a date range that fits your minimum trip duration, and a maximum duration of at least the minimum.");
            return;
        }
        setError(null); setSaving(true); inFlight.current = true;
        try {
            await GroupSessionService.submitPreferences(sessionId, {
                departureAirport: departure[0], destinationAirports: destinations,
                categories, budgetMax: budget, timeRangeStart: start, timeRangeEnd: end,
                travelDurationMin: minDays, travelDurationMax: maxDays,
            });
            navigate(`/sessions/${sessionId}`);
        } catch {
            setError("Could not save your preferences. Please try again.");
        } finally {
            setSaving(false); inFlight.current = false;
        }
    };

    const showLoading = useMinimumLoading(loading);

    const inputClass = "mt-2 w-full rounded-xl border border-violet-100 bg-white p-3 text-gray-800";
    return <main className="min-h-screen bg-gradient-to-b from-[#f6f8ff] to-[#dde5ff] px-5 py-12 text-[#232323]">
        <div className="mx-auto max-w-4xl">
            <button className="mb-8 text-violet-700" onClick={() => navigate(`/sessions/${sessionId}`)}>← Back to session</button>
            <h1 className="fw-unblur text-4xl font-bold">Share your preferences.</h1>
            <p className="mt-3 mb-8 text-[#858ca7]">Dein Startpunkt, deine Wunschziele und dein Budget. Wir finden gemeinsame Reisevorschläge und zeigen euch transparent, wo Kompromisse nötig sind.</p>
            {error && <p role="alert" className="mb-6 rounded-xl bg-red-50 p-4 text-red-700">{error}</p>}
            {showLoading && !error && <GroupSkeleton variant="preferences" label="Deine Reisewünsche werden geladen"/>}
            {!showLoading && <form className="fw-reveal" onSubmit={submit}>
                <fieldset disabled={loading || saving} className="grid gap-6 md:grid-cols-2 disabled:opacity-60">
                    <section className="rounded-3xl bg-white/80 p-6 md:col-span-2">
                        <div className="grid gap-8 md:grid-cols-2">
                            <div><GroupAirportInput label="Dein Abflughafen" airports={airports} selected={departure} onChange={setDeparture}/>
                                <p className="mt-3 text-sm text-gray-500">Jede Person kann von einem anderen Flughafen starten.</p></div>
                            <div><GroupAirportInput label="Deine Wunschziele" airports={airports} selected={destinations} onChange={setDestinations} multiple/>
                                <p className="mt-3 text-sm text-gray-500">Optional. Mehrere Städte möglich; wir berücksichtigen sie als Wünsche.</p></div>
                        </div>
                    </section>
                    <section className="rounded-3xl bg-white/60 p-6">
                        <h2 className="mb-5 text-xl font-semibold">Travel dates</h2>
                        <label className="block mb-4">Earliest departure<input className={inputClass} type="date" required min={localDate(today)} value={start} onChange={e => setStart(e.target.value)}/></label>
                        <label className="block">Latest return<input className={inputClass} type="date" required min={start} value={end} onChange={e => setEnd(e.target.value)}/></label>
                    </section>
                    <section className="rounded-3xl bg-white/60 p-6">
                        <h2 className="mb-5 text-xl font-semibold">Budget & duration</h2>
                        <label className="block mb-4">Maximum return flight price (€ per person)<input className={inputClass} type="number" min="1" step="1" required value={budget} onChange={e => setBudget(Number(e.target.value))}/></label>
                        <div className="grid grid-cols-2 gap-4">
                            <label>Minimum nights<input className={inputClass} type="number" min="1" step="1" required value={minDays} onChange={e => setMinDays(Number(e.target.value))}/></label>
                            <label>Maximum nights<input className={inputClass} type="number" min={minDays} step="1" required value={maxDays} onChange={e => setMaxDays(Number(e.target.value))}/></label>
                        </div>
                    </section>
                    <section className="rounded-3xl bg-white/60 p-6 md:col-span-2">
                        <h2 className="text-xl font-semibold">Destination types</h2>
                        <p className="mt-1 mb-5 text-sm text-[#858ca7]">Select your favourites, or leave empty for any destination.</p>
                        <div className="flex flex-wrap gap-3">{Object.values(DestinationCategory).map(category => <button
                            type="button" key={category} aria-pressed={categories.includes(category)}
                            onClick={() => setCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category])}
                            className={`rounded-xl px-4 py-3 font-semibold ${categories.includes(category) ? "bg-[#a87af0] text-white" : "bg-white text-gray-700"}`}>
                            {CategoryIconMap[category]} {CategoryLabelMap[category]}
                        </button>)}</div>
                    </section>
                    <p className="text-sm text-gray-500 self-center">Bei abweichenden Wünschen vergleichen wir die verfügbaren Alternativen. Deine Angaben bleiben als Wunschwerte sichtbar.</p>
                    <button type="submit" className="rounded-full bg-[#a87af0] px-8 py-4 font-bold text-white md:col-start-2 disabled:opacity-60">{saving ? "Saving…" : "Save preferences"}</button>
                </fieldset>
            </form>}
        </div>
    </main>;
}
